import React, { useMemo, useState } from "react";
import { ShieldCheck,ShieldOff } from "lucide-react";

import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { Dialog } from "components/ui/Dialog";
import { PageHeader } from "components/ui/PageHeader";
import { Pill } from "components/ui/Pill";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { useMobileAppErrors } from "features/mobileApp/hooks/useMobileAppErrors";
import { useMobileDeviceAction } from "features/mobileApp/hooks/useMobileDeviceAction";
import { useMobileDevices } from "features/mobileApp/hooks/useMobileDevices";

import styles from "./MobileAppPage.module.css";

const TAB_OPTIONS = [
  { label: "Devices", value: "devices" },
  { label: "Error Reports", value: "errors" },
];

const YES = { color: "#006546", bg: "#e7efeb", border: "#006546" };
const NO = { color: "#26252c", bg: "#f1f1f3", border: "#d9d8df" };
const REVOKED = { color: "#b70000", bg: "#ffe8e8", border: "#b70000" };

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString("en-IE") : "—";

/**
 * Settings → Mobile App: admin view over the clinician app's two tables —
 * registered devices (mobile_device, with revoke/restore for lost phones)
 * and client error reports (mobile_app_error crash dashboard).
 */
const MobileAppPage = () => {
  const [tab, setTab] = useState("devices");

  return (
    <div className={styles.content}>
      <PageHeader title="Mobile App" />

      <TabSwitcher
        options={TAB_OPTIONS}
        value={tab}
        onChange={setTab}
        fullWidth
      />

      {tab === "devices" ? <DevicesTab /> : <ErrorsTab />}
    </div>
  );
};

const DevicesTab = () => {
  const { data: devices = [], isLoading } = useMobileDevices();
  const deviceAction = useMobileDeviceAction();
  const [pendingRevoke, setPendingRevoke] = useState(null);

  const columns = useMemo(
    () => [
      {
        id: "device",
        header: "Device",
        width: "minmax(160px, 1fr)",
        render: (row) => (
          <div>
            <div className={styles.deviceName}>{row.deviceName || "—"}</div>
            <div className={styles.deviceModel}>{row.deviceModel || ""}</div>
          </div>
        ),
      },
      {
        id: "platform",
        header: "Platform",
        width: "90px",
        render: (row) => row.platform || "—",
      },
      {
        id: "email",
        header: "Registered To",
        width: "minmax(180px, 1fr)",
        render: (row) => (
          <div>
            <div>{row.email || "—"}</div>
            <div className={styles.deviceModel}>{row.mobile || ""}</div>
          </div>
        ),
      },
      {
        id: "verified",
        header: "Verified",
        width: "100px",
        render: (row) => (
          <Pill {...(row.verified ? YES : NO)}>
            {row.verified ? "Yes" : "No"}
          </Pill>
        ),
      },
      {
        id: "lastVerifiedAt",
        header: "Last Verified",
        width: "150px",
        render: (row) => formatDateTime(row.lastVerifiedAt),
      },
      {
        id: "pushEnabled",
        header: "Push",
        width: "90px",
        render: (row) => (
          <Pill {...(row.pushEnabled ? YES : NO)}>
            {row.pushEnabled ? "On" : "Off"}
          </Pill>
        ),
      },
      {
        id: "disabled",
        header: "Status",
        width: "100px",
        render: (row) => (
          <Pill {...(row.disabled ? REVOKED : YES)}>
            {row.disabled ? "Revoked" : "Active"}
          </Pill>
        ),
      },
    ],
    []
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Revoke",
        icon: ShieldOff,
        onClick: (row) => setPendingRevoke(row),
        visibleWhen: (row) => !row.disabled,
      },
      {
        label: "Restore",
        icon: ShieldCheck,
        onClick: (row) =>
          deviceAction.mutate({ id: row.id, action: "restore" }),
        visibleWhen: (row) => row.disabled,
      },
    ],
    [deviceAction]
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={devices}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        emptyTitle="No devices registered"
        emptyText="Devices appear here once a clinician verifies the mobile app."
        rowActions={rowActions}
      />
      <ConfirmDialog
        isOpen={!!pendingRevoke}
        onClose={() => setPendingRevoke(null)}
        onConfirm={() => {
          deviceAction.mutate(
            { id: pendingRevoke.id, action: "revoke" },
            { onSettled: () => setPendingRevoke(null) }
          );
        }}
        isLoading={deviceAction.isPending}
        title="Revoke this device?"
        message={`"${pendingRevoke?.deviceName || "This device"}" will no longer pass the app's device gate and will stop receiving push notifications. You can restore it later.`}
        confirmLabel="Revoke device"
        confirmVariant="danger"
      />
    </>
  );
};

const ErrorsTab = () => {
  const [page, setPage] = useState(1);
  const rows = 20;
  const { data, isLoading } = useMobileAppErrors({ page, rows });
  const [selectedError, setSelectedError] = useState(null);

  const columns = useMemo(
    () => [
      {
        id: "created",
        header: "When",
        width: "180px",
        render: (row) => formatDateTime(row.created),
      },
      {
        id: "userEmail",
        header: "User",
        width: "210px",
        render: (row) => row.userEmail || "—",
      },
      {
        id: "remoteIp",
        header: "IP Address",
        width: "160px",
        render: (row) => row.remoteIp || "—",
      },
      {
        id: "route",
        header: "Screen",
        width: "120px",
        render: (row) => row.route || "—",
      },
      {
        /*
         * Every width here is bounded on purpose. The table sizes itself to
         * max-content, so an unbounded (or fr) Error column stretches to the
         * length of the longest exception string and pushes the rest of the
         * table off screen.
         *
         * The seven tracks now total 1310px, which is wider than the ~833px the
         * settings layout leaves at 1440px — so this table scrolls sideways by
         * design, having been cut too aggressively to be readable. User is wide
         * enough for a whole address rather than an ellipsis, since the point of
         * the column is knowing who hit the error.
         * Each cell still truncates with an ellipsis, DataTableCell tooltips
         * whatever it clipped, and the row opens the full message and stack trace.
         */
        id: "errorMessage",
        header: "Error",
        width: "400px",
        testId: "mobile-error-message",
        render: (row) => row.errorMessage || "—",
      },
      {
        id: "device",
        header: "Device",
        width: "130px",
        render: (row) =>
          [row.deviceModel, row.platform].filter(Boolean).join(" · ") || "—",
      },
      {
        id: "appVersion",
        header: "Version",
        width: "110px",
        render: (row) => row.appVersion || "—",
      },
    ],
    []
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={data?.data ?? []}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        emptyTitle="No error reports"
        emptyText="Client-side errors from the mobile app will appear here."
        onRowClick={(row) => setSelectedError(row)}
        pagination
        currentPage={page}
        rowsPerPage={rows}
        totalCount={data?.totalRecords ?? 0}
        onPageChange={setPage}
      />
      <Dialog
        open={!!selectedError}
        onClose={() => setSelectedError(null)}
        title={`Error #${selectedError?.id ?? ""}`}
        width={720}
      >
        {selectedError && (
          <div className={styles.errorDetail}>
            <p>
              <strong>{formatDateTime(selectedError.created)}</strong>
              {" · "}
              {selectedError.userEmail || "unknown user"}
              {" · "}
              {selectedError.route || "unknown screen"}
              {" · v"}
              {selectedError.appVersion || "?"}
            </p>
            <p>
              {[selectedError.deviceModel, selectedError.platform]
                .filter(Boolean)
                .join(" · ")}
              {selectedError.remoteIp ? ` · ${selectedError.remoteIp}` : ""}
            </p>
            <p className={styles.errorMessageFull}>
              {selectedError.errorMessage}
            </p>
            <pre className={styles.stackTrace}>
              {selectedError.stackTrace || "No stack trace recorded."}
            </pre>
          </div>
        )}
      </Dialog>
    </>
  );
};

export { MobileAppPage };
